import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import {
  getFactories,
  addFactory,
  updateFactoryById,
  removeFactory,
  subscribeFactories,
} from '../../../data/factoriesData';
import { supabase } from '../../../lib/supabase';

// ─── CSV helpers ─────────────────────────────────────────────────────────────
const FACTORY_CSV_HEADERS = ['FactoryShortCode', 'FactoryName', 'Location', 'State', 'Factory_Pincode', 'Status', 'Remark'];

const FACTORY_SAMPLE_ROWS = [
  ['SGM001', 'Shree Ganesh Sugar Mills', 'Kolhapur, Maharashtra', 'Maharashtra', '416001', 'Active', ''],
  ['MRS002', 'Maharashtra Rajya Sahakari', 'Sangli, Maharashtra', 'Maharashtra', '416416', 'Inactive', 'update'],
  ['NEW003', 'New Factory Ltd', 'Nagpur, Maharashtra', 'Maharashtra', '440001', 'Active', 'update'],
];

function downloadSampleCSV() {
  const rows = [FACTORY_CSV_HEADERS, ...FACTORY_SAMPLE_ROWS];
  const csv = rows?.map(r => r?.join(','))?.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'factory_management_sample.csv';
  a?.click();
  URL.revokeObjectURL(url);
}

function downloadCurrentDataCSV(factories) {
  const headers = FACTORY_CSV_HEADERS;
  const dataRows = factories?.map(f => [
    f?.shortCode || '',
    f?.name || '',
    f?.location || '',
    f?.state || '',
    f?.pincode || '',
    f?.status || '',
    '',
  ]?.map(v => `"${String(v)?.replace(/"/g, '""')}"`)?.join(','));
  const csv = [headers?.join(','), ...dataRows]?.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const date = new Date()?.toISOString()?.split('T')?.[0];
  a.download = `factories_current_data_${date}.csv`;
  a?.click();
  URL.revokeObjectURL(url);
}

function parseCSV(text) {
  const lines = text?.trim()?.split('\n')?.filter(l => l?.trim());
  if (lines?.length < 2) return [];
  const headers = lines?.[0]?.split(',')?.map(h => h?.trim());
  return lines?.slice(1)?.map(line => {
    const vals = line?.split(',')?.map(v => v?.trim());
    const obj = {};
    headers?.forEach((h, i) => { obj[h] = vals?.[i] ?? ''; });
    return obj;
  });
}

function downloadResultsCSV(rows, resultRows) {
  const headers = [...FACTORY_CSV_HEADERS, 'Update_Status', 'Message'];
  const dataRows = rows?.map((row, i) => {
    const r = resultRows?.[i] || {};
    return [
      row?.FactoryShortCode || '',
      row?.FactoryName || '',
      row?.Location || '',
      row?.State || '',
      row?.Factory_Pincode || '',
      row?.Status || '',
      row?.Remark || '',
      r?.status || '',
      r?.message || '',
    ]?.map(v => `"${String(v)?.replace(/"/g, '""')}"`)?.join(',');
  });
  const csv = [headers?.join(','), ...dataRows]?.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const date = new Date()?.toISOString()?.split('T')?.[0];
  a.download = `upload_results_${date}.csv`;
  a?.click();
  URL.revokeObjectURL(url);
}

// ─── CSV Upload Modal ─────────────────────────────────────────────────────────
const FactoryCSVUploadModal = ({ onClose, onUpload }) => {
  const fileRef = useRef(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleFile = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file?.name?.endsWith('.csv')) { setError('Please upload a .csv file.'); return; }
    setError('');
    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const rows = parseCSV(ev?.target?.result);
        const processed = await onUpload(rows);
        setResult(processed);
        downloadResultsCSV(rows, processed?.resultRows);
      } catch (err) {
        console.error('CSV upload error:', err);
        setError('Failed to parse or upload CSV. Please check the format.');
      }
      setLoading(false);
    };
    reader?.readAsText(file);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
      <div className="rounded-2xl shadow-xl w-full max-w-md" style={{ backgroundColor: '#fdf6ec', border: '1px solid #e5d9cc' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#e5d9cc', backgroundColor: '#f8f1e9', borderRadius: '1rem 1rem 0 0' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
              <Icon name="Upload" size={18} color="#d97706" />
            </div>
            <h2 className="text-base font-bold" style={{ color: '#1f2937' }}>Bulk Upload — Factory Management</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-amber-100">
            <Icon name="X" size={18} color="#6b7280" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="rounded-lg p-3 text-xs" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
            <p className="font-semibold mb-1" style={{ color: '#92400e' }}>Required CSV Columns:</p>
            <p style={{ color: '#78350f' }}>FactoryShortCode, FactoryName, Location, State, Factory_Pincode, Status (Active/Inactive), Remark</p>
            <p className="mt-1.5 font-medium" style={{ color: '#92400e' }}>💡 State is mandatory. Factory Pincode is mandatory (6-digit Indian pincode). Remark = "update" → updates/adds record. Blank = skip.</p>
          </div>
          <button onClick={downloadSampleCSV} className="flex items-center gap-2 text-sm font-semibold underline" style={{ color: '#d97706' }}>
            <Icon name="Download" size={15} />
            Download Sample CSV
          </button>
          {!result && (
            <div
              onClick={() => fileRef?.current?.click()}
              className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed cursor-pointer py-8 transition-colors hover:bg-amber-50"
              style={{ borderColor: '#f59e0b' }}
            >
              <Icon name="FileText" size={32} color="#f59e0b" />
              <p className="text-sm font-semibold" style={{ color: '#374151' }}>Click to select CSV file</p>
              <p className="text-xs" style={{ color: '#9ca3af' }}>.csv files only</p>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
            </div>
          )}
          {loading && <p className="text-sm text-center" style={{ color: '#6b7280' }}>Processing…</p>}
          {error && <p className="text-sm font-medium" style={{ color: '#dc2626' }}>{error}</p>}
          {result && (
            <div className="rounded-xl p-4 space-y-2" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <div className="flex items-center gap-2 mb-1">
                <Icon name="CheckCircle" size={18} color="#16a34a" />
                <p className="font-bold text-sm" style={{ color: '#15803d' }}>Upload Complete</p>
              </div>
              <p className="text-base font-semibold" style={{ color: '#166534' }}>
                {result?.updated} row{result?.updated !== 1 ? 's' : ''} updated successfully, {result?.skipped} row{result?.skipped !== 1 ? 's' : ''} skipped
              </p>
              {result?.invalid > 0 && <p className="text-sm" style={{ color: '#b45309' }}>⚠️ Invalid: <strong>{result?.invalid}</strong> row{result?.invalid !== 1 ? 's' : ''} (missing required fields)</p>}
              <div className="flex items-center gap-1.5 mt-2 pt-2 border-t" style={{ borderColor: '#bbf7d0' }}>
                <Icon name="Download" size={14} color="#16a34a" />
                <p className="text-xs" style={{ color: '#15803d' }}>Results CSV downloaded automatically</p>
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border text-sm font-semibold" style={{ borderColor: '#d1c4b0', color: '#374151', backgroundColor: '#ffffff' }}>
            {result ? 'Close' : 'Cancel'}
          </button>
          {result && (
            <button onClick={() => { setResult(null); if (fileRef?.current) fileRef.current.value = ''; }} className="flex-1 py-2.5 rounded-lg text-sm font-bold" style={{ backgroundColor: '#f59e0b', color: '#000000' }}>
              Upload Another
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Star Rating Component ────────────────────────────────────────────────────
const StarRating = ({ value, onChange }) => {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5]?.map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="transition-transform hover:scale-110"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill={(hovered || value) >= star ? '#f59e0b' : 'none'}
            stroke={(hovered || value) >= star ? '#f59e0b' : '#6b7280'}
            strokeWidth="1.5"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>
      ))}
      {value > 0 && <span className="text-xs font-semibold ml-1" style={{ color: '#f59e0b' }}>{value}/5</span>}
    </div>
  );
};

// ─── Image Upload Field ───────────────────────────────────────────────────────
const ImageUploadField = ({ label, value, preview, onChange, onRemove, id }) => {
  const inputRef = useRef(null);
  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5" style={{ color: '#9ca3af' }}>{label}</label>
      {preview ? (
        <div className="relative rounded-lg overflow-hidden border border-gray-600" style={{ backgroundColor: '#1a2332' }}>
          <img src={preview} alt={label} className="w-full h-32 object-cover" />
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#dc2626' }}
          >
            <Icon name="X" size={12} color="#ffffff" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => inputRef?.current?.click()}
          className="flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed cursor-pointer py-5 transition-colors"
          style={{ borderColor: '#374151', backgroundColor: '#111827' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#f59e0b'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#374151'; }}
        >
          <Icon name="ImagePlus" size={22} color="#6b7280" />
          <p className="text-xs" style={{ color: '#6b7280' }}>Click to upload</p>
          <p className="text-xs" style={{ color: '#4b5563' }}>PNG, JPG up to 5MB</p>
        </div>
      )}
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onChange}
      />
    </div>
  );
};

// ─── Rich Factory Form Modal ──────────────────────────────────────────────────
const FORM_TABS = [
  { id: 'details', label: 'Details', icon: 'FileText' },
  { id: 'photos', label: 'Photos', icon: 'Image' },
  { id: 'location', label: 'Location', icon: 'MapPin' },
  { id: 'reviews', label: 'Reviews', icon: 'Star' },
];

const EMPTY_REVIEW = { reviewerName: '', reviewDate: '', rating: 0, reviewText: '' };

const RichFactoryFormModal = ({ editingFactory, onClose, onSave }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [formData, setFormData] = useState({
    // Existing fields
    name: editingFactory?.name || '',
    shortCode: editingFactory?.shortCode || '',
    factoryGstin: editingFactory?.factory_gstin || '',
    location: editingFactory?.location || '',
    pincode: editingFactory?.pincode || '',
    // Details tab
    productDescription: editingFactory?.productDescription || '',
    grade: editingFactory?.grade || '',
    zone: editingFactory?.zone || '',
    // Location tab extras
    city: editingFactory?.city || '',
    state: editingFactory?.state || '',
    bestRoute: editingFactory?.bestRoute || '',
    estimatedTravelTime: editingFactory?.estimatedTravelTime || '',
    latitude: editingFactory?.latitude ?? null,
    longitude: editingFactory?.longitude ?? null,
    // Reviews
    reviews: editingFactory?.reviews || [],
  });

  const [photoPreviews, setPhotoPreviews] = useState({
    factoryPhoto: editingFactory?.factoryPhotoUrl || null,
    productPhoto: editingFactory?.productPhotoUrl || null,
    packingPhoto: editingFactory?.packingPhotoUrl || null,
  });
  const [photoFiles, setPhotoFiles] = useState({ factoryPhoto: null, productPhoto: null, packingPhoto: null });

  const handleField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  // ── Directory picker: State -> District -> searchable Factory, sourced from
  // mi_mills (same 626-mill master data /mandi and Market Intelligence use).
  // Optional assist only — every field it fills stays freely editable, since
  // not every real-world factory is in this master list.
  const [directoryMills, setDirectoryMills] = useState([]);
  const [directoryFactories, setDirectoryFactories] = useState([]);
  const [pickState, setPickState] = useState('');
  const [pickDistrict, setPickDistrict] = useState('');
  const [pickSearch, setPickSearch] = useState('');

  useEffect(() => {
    (async () => {
      const [millsRes, dirRes] = await Promise.all([
        supabase.from('mi_mills').select('id, canonical_name, mill_code, pincode, source_state, source_district'),
        supabase.from('internal_directory_factories').select('mill_code, trusted_pincode, source_pincode, factory_name, city'),
      ]);
      setDirectoryMills(millsRes?.data || []);
      setDirectoryFactories(dirRes?.data || []);
    })();
  }, []);

  const pickStates = [...new Set(directoryMills.map(m => m.source_state))].filter(Boolean).sort();
  const pickDistricts = pickState
    ? [...new Set(directoryMills.filter(m => m.source_state === pickState).map(m => m.source_district))].filter(Boolean).sort()
    : [];
  const pickResults = pickSearch.trim().length >= 2
    ? directoryMills
        .filter(m => (!pickState || m.source_state === pickState) && (!pickDistrict || m.source_district === pickDistrict))
        .filter(m => m.canonical_name?.toLowerCase()?.includes(pickSearch.toLowerCase()))
        .slice(0, 8)
    : [];

  const handlePickMill = (mill) => {
    const dirMatch = directoryFactories.find(f =>
      f.mill_code === mill.mill_code || f.trusted_pincode === mill.pincode || f.source_pincode === mill.pincode
    );
    setFormData(prev => ({
      ...prev,
      name: dirMatch?.factory_name || mill.canonical_name,
      city: dirMatch?.city || mill.source_district || '',
      state: mill.source_state || '',
      location: `${dirMatch?.city || mill.source_district || ''}, ${mill.source_state || ''}`,
      pincode: mill.pincode || prev.pincode,
    }));
    setPickSearch('');
  };

  // Auto-resolve latitude/longitude from the pincodes table whenever a valid
  // 6-digit pincode is set (whether picked above or typed manually).
  useEffect(() => {
    const pin = formData?.pincode;
    if (!/^\d{6}$/.test(pin || '')) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from('pincodes').select('latitude, longitude').eq('pincode', pin).maybeSingle();
      if (!cancelled && data?.latitude != null && data?.longitude != null) {
        setFormData(prev => ({ ...prev, latitude: data.latitude, longitude: data.longitude }));
      }
    })();
    return () => { cancelled = true; };
  }, [formData?.pincode]);

  const handlePhotoChange = (key, e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setPhotoFiles(prev => ({ ...prev, [key]: file }));
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreviews(prev => ({ ...prev, [key]: reader?.result }));
    reader?.readAsDataURL(file);
  };

  const removePhoto = (key) => {
    setPhotoFiles(prev => ({ ...prev, [key]: null }));
    setPhotoPreviews(prev => ({ ...prev, [key]: null }));
  };

  // Reviews
  const addReview = () => setFormData(prev => ({ ...prev, reviews: [...prev?.reviews, { ...EMPTY_REVIEW }] }));
  const removeReview = (idx) => setFormData(prev => ({ ...prev, reviews: prev?.reviews?.filter((_, i) => i !== idx) }));
  const updateReview = (idx, field, value) => setFormData(prev => ({
    ...prev,
    reviews: prev?.reviews?.map((r, i) => i === idx ? { ...r, [field]: value } : r),
  }));

  const isValid = formData?.name?.trim() && formData?.location?.trim() && /^\d{6}$/?.test(formData?.pincode?.trim());

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!isValid || saving) return;
    setSaving(true);

    const uploadPhoto = async (key, label) => {
      const file = photoFiles?.[key];
      if (!file) {
        const existing = photoPreviews?.[key];
        return (existing && !existing.startsWith('data:')) ? existing : null;
      }
      const ext = file?.name?.split('.')?.pop() || 'jpg';
      const folder = (formData?.shortCode?.trim() || 'unknown').toLowerCase();
      const path = `${folder}/${Date.now()}-${label}.${ext}`;
      const { error } = await supabase.storage.from('factory-photos').upload(path, file, { upsert: true });
      if (error) { console.error(`Upload error (${key}):`, error.message); return null; }
      const { data } = supabase.storage.from('factory-photos').getPublicUrl(path);
      return data?.publicUrl || null;
    };

    const [factoryPhotoUrl, productPhotoUrl, packingPhotoUrl] = await Promise.all([
      uploadPhoto('factoryPhoto', 'factory'),
      uploadPhoto('productPhoto', 'product'),
      uploadPhoto('packingPhoto', 'packing'),
    ]);

    onSave({
      name: formData?.name?.trim(),
      shortCode: formData?.shortCode?.trim(),
      factoryGstin: formData?.factoryGstin?.trim(),
      location: formData?.location?.trim(),
      pincode: formData?.pincode?.trim(),
      productDescription: formData?.productDescription,
      grade: formData?.grade,
      zone: formData?.zone,
      city: formData?.city,
      state: formData?.state,
      bestRoute: formData?.bestRoute,
      estimatedTravelTime: formData?.estimatedTravelTime,
      latitude: formData?.latitude,
      longitude: formData?.longitude,
      reviews: formData?.reviews,
      factoryPhotoUrl,
      productPhotoUrl,
      packingPhotoUrl,
    });

    setSaving(false);
  };

  const inputCls = "w-full px-3 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors";
  const inputStyle = { backgroundColor: '#111827', borderColor: '#374151', color: '#f3f4f6' };
  const labelCls = "block text-xs font-semibold mb-1.5";
  const labelStyle = { color: '#9ca3af' };
  const sectionHeadingCls = "flex items-center gap-2 text-sm font-bold mb-4 pb-2 border-b";
  const sectionHeadingStyle = { color: '#f3f4f6', borderColor: '#1f2937' };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-3" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div className="rounded-2xl shadow-2xl w-full flex flex-col" style={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', maxWidth: '580px', maxHeight: '92vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 rounded-t-2xl flex-shrink-0" style={{ backgroundColor: '#1e293b', borderBottom: '1px solid #334155' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#f59e0b22' }}>
              <Icon name={editingFactory ? 'Edit' : 'Plus'} size={16} color="#f59e0b" />
            </div>
            <div>
              <h2 className="text-base font-bold" style={{ color: '#f3f4f6' }}>{editingFactory ? 'Edit Factory' : 'Add New Factory'}</h2>
              <p className="text-xs" style={{ color: '#64748b' }}>Fill in all sections for complete buyer-facing details</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-slate-700">
            <Icon name="X" size={18} color="#94a3b8" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-shrink-0" style={{ backgroundColor: '#1e293b', borderBottom: '1px solid #334155' }}>
          {FORM_TABS?.map(tab => (
            <button
              key={tab?.id}
              type="button"
              onClick={() => setActiveTab(tab?.id)}
              className="flex-1 flex flex-col items-center gap-1 py-3 text-xs font-semibold transition-colors relative"
              style={{
                color: activeTab === tab?.id ? '#f59e0b' : '#64748b',
                backgroundColor: activeTab === tab?.id ? '#0f172a' : 'transparent',
              }}
            >
              <Icon name={tab?.icon} size={15} color={activeTab === tab?.id ? '#f59e0b' : '#64748b'} />
              {tab?.label}
              {activeTab === tab?.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t" style={{ backgroundColor: '#f59e0b' }} />
              )}
            </button>
          ))}
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto flex-1 px-5 py-5 space-y-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#334155 transparent' }}>

          {/* ── DETAILS TAB ── */}
          {activeTab === 'details' && (
            <div className="space-y-4">
              <div className={sectionHeadingCls} style={sectionHeadingStyle}>
                <Icon name="FileText" size={15} color="#f59e0b" />
                Basic Information
              </div>

              {/* Directory picker — optional assist, fills fields below from the master mill list */}
              <div className="rounded-xl p-3 space-y-2.5" style={{ backgroundColor: '#111827', border: '1px solid #334155' }}>
                <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: '#9ca3af' }}>
                  <Icon name="Search" size={13} color="#f59e0b" />
                  Pick from directory (optional) — fills fields below
                </p>
                <div className="grid grid-cols-2 gap-2.5">
                  <select
                    value={pickState}
                    onChange={e => { setPickState(e.target.value); setPickDistrict(''); }}
                    className={inputCls}
                    style={inputStyle}
                  >
                    <option value="">State</option>
                    {pickStates.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select
                    value={pickDistrict}
                    onChange={e => setPickDistrict(e.target.value)}
                    disabled={!pickState}
                    className={inputCls}
                    style={{ ...inputStyle, opacity: pickState ? 1 : 0.5, cursor: pickState ? 'auto' : 'not-allowed' }}
                  >
                    <option value="">{pickState ? 'District' : 'Select a state first'}</option>
                    {pickDistricts.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <input
                  type="text"
                  value={pickSearch}
                  onChange={e => setPickSearch(e.target.value)}
                  disabled={!pickState}
                  placeholder={pickState ? 'Search factory name...' : 'Select a state first'}
                  className={inputCls}
                  style={{ ...inputStyle, opacity: pickState ? 1 : 0.5, cursor: pickState ? 'auto' : 'not-allowed' }}
                />
                {pickResults.length > 0 && (
                  <div className="rounded-lg overflow-hidden" style={{ border: '1px solid #334155' }}>
                    {pickResults.map(m => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => handlePickMill(m)}
                        className="w-full text-left px-3 py-2 text-xs transition-colors hover:bg-slate-700"
                        style={{ color: '#f3f4f6', borderTop: '1px solid #334155' }}
                      >
                        {m.canonical_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className={labelCls} style={labelStyle}>Factory Short Code</label>
                <input
                  type="text"
                  value={formData?.shortCode}
                  onChange={e => handleField('shortCode', e?.target?.value?.toUpperCase())}
                  placeholder="e.g., SGM001"
                  className={inputCls}
                  style={inputStyle}
                />
              </div>
                <div>
  <label className={labelCls} style={labelStyle}>Factory GSTIN</label>
  <input
    type="text"
    value={formData?.factoryGstin}
    onChange={e => handleField('factoryGstin', e?.target?.value?.toUpperCase())}
    placeholder="e.g., 29AABCS1234C1Z5"
    className={inputCls}
    style={inputStyle}
  />
</div>
              <div>
                <label className={labelCls} style={labelStyle}>Factory Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  value={formData?.name}
                  onChange={e => handleField('name', e?.target?.value)}
                  placeholder="e.g., Shree Ganesh Sugar Mills"
                  className={inputCls}
                  style={inputStyle}
                />
              </div>

              <div>
                <label className={labelCls} style={labelStyle}>Location (City, State) <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  value={formData?.location}
                  onChange={e => handleField('location', e?.target?.value)}
                  placeholder="e.g., Kolhapur, Maharashtra"
                  className={inputCls}
                  style={inputStyle}
                />
              </div>

              <div className="pt-2 border-t" style={{ borderColor: '#1e293b' }}>
                <div className={sectionHeadingCls} style={sectionHeadingStyle}>
                  <Icon name="Package" size={15} color="#f59e0b" />
                  Product Details
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={labelCls} style={labelStyle}>Product Description / Quality Specs</label>
                    <textarea
                      value={formData?.productDescription}
                      onChange={e => handleField('productDescription', e?.target?.value)}
                      placeholder="Describe product quality, specifications, ICUMSA levels, moisture content, etc."
                      rows={4}
                      className={inputCls + ' resize-none'}
                      style={inputStyle}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls} style={labelStyle}>Grade</label>
                      <input
                        type="text"
                        value={formData?.grade}
                        onChange={e => handleField('grade', e?.target?.value)}
                        placeholder="e.g., S-30, ICUMSA 45"
                        className={inputCls}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label className={labelCls} style={labelStyle}>Zone</label>
                      <input
                        type="text"
                        value={formData?.zone}
                        onChange={e => handleField('zone', e?.target?.value)}
                        placeholder="e.g., Western Zone"
                        className={inputCls}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── PHOTOS TAB ── */}
          {activeTab === 'photos' && (
            <div className="space-y-5">
              <div className={sectionHeadingCls} style={sectionHeadingStyle}>
                <Icon name="Image" size={15} color="#f59e0b" />
                Factory & Product Photos
              </div>
              <p className="text-xs" style={{ color: '#64748b' }}>Upload photos that will appear in the buyer-facing "Photos" tab of the Complete Details modal.</p>

              <ImageUploadField
                label="Factory Photo"
                id="factory-photo"
                value={photoFiles?.factoryPhoto}
                preview={photoPreviews?.factoryPhoto}
                onChange={e => handlePhotoChange('factoryPhoto', e)}
                onRemove={() => removePhoto('factoryPhoto')}
              />

              <ImageUploadField
                label="Product Photo (Sugar)"
                id="product-photo"
                value={photoFiles?.productPhoto}
                preview={photoPreviews?.productPhoto}
                onChange={e => handlePhotoChange('productPhoto', e)}
                onRemove={() => removePhoto('productPhoto')}
              />

              <ImageUploadField
                label="Packing / Bag Photo"
                id="packing-photo"
                value={photoFiles?.packingPhoto}
                preview={photoPreviews?.packingPhoto}
                onChange={e => handlePhotoChange('packingPhoto', e)}
                onRemove={() => removePhoto('packingPhoto')}
              />
            </div>
          )}

          {/* ── LOCATION TAB ── */}
          {activeTab === 'location' && (
            <div className="space-y-4">
              <div className={sectionHeadingCls} style={sectionHeadingStyle}>
                <Icon name="MapPin" size={15} color="#f59e0b" />
                Location Details
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls} style={labelStyle}>City <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="text"
                    value={formData?.city}
                    onChange={e => handleField('city', e?.target?.value)}
                    placeholder="e.g., Kolhapur"
                    className={inputCls}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>State <span style={{ color: '#ef4444' }}>*</span></label>
                  <input
                    type="text"
                    value={formData?.state}
                    onChange={e => handleField('state', e?.target?.value)}
                    placeholder="e.g., Maharashtra"
                    className={inputCls}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls} style={labelStyle}>Factory Pincode <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  value={formData?.pincode}
                  onChange={e => handleField('pincode', e?.target?.value?.replace(/\D/g, '')?.slice(0, 6))}
                  placeholder="e.g. 411001"
                  maxLength={6}
                  className={inputCls}
                  style={inputStyle}
                />
                {formData?.pincode && !/^\d{6}$/?.test(formData?.pincode) && (
                  <p className="text-xs mt-1" style={{ color: '#ef4444' }}>Must be a 6-digit Indian pincode</p>
                )}
              </div>

              {formData?.latitude != null && formData?.longitude != null && (
                <div className="rounded-lg overflow-hidden" style={{ border: '1px solid #334155' }}>
                  <iframe
                    title="Factory location"
                    width="100%"
                    height="200"
                    style={{ border: 0, display: 'block' }}
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${formData.longitude - 0.02},${formData.latitude - 0.02},${formData.longitude + 0.02},${formData.latitude + 0.02}&marker=${formData.latitude},${formData.longitude}`}
                  />
                  <p className="text-[11px] px-2 py-1.5" style={{ color: '#64748b', backgroundColor: '#111827' }}>
                    {formData.latitude.toFixed(4)}, {formData.longitude.toFixed(4)} — resolved from pincode {formData.pincode}
                  </p>
                </div>
              )}

              <div className="pt-2 border-t" style={{ borderColor: '#1e293b' }}>
                <div className={sectionHeadingCls} style={sectionHeadingStyle}>
                  <Icon name="Navigation" size={15} color="#f59e0b" />
                  Route & Travel Info
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={labelCls} style={labelStyle}>Best Route</label>
                    <input
                      type="text"
                      value={formData?.bestRoute}
                      onChange={e => handleField('bestRoute', e?.target?.value)}
                      placeholder="e.g., Via National Highway NH-48"
                      className={inputCls}
                      style={inputStyle}
                    />
                    <p className="text-xs mt-1" style={{ color: '#4b5563' }}>Primary route description shown to buyers</p>
                  </div>

                  <div>
                    <label className={labelCls} style={labelStyle}>Estimated Travel Time</label>
                    <input
                      type="text"
                      value={formData?.estimatedTravelTime}
                      onChange={e => handleField('estimatedTravelTime', e?.target?.value)}
                      placeholder="e.g., 3-4 hours"
                      className={inputCls}
                      style={inputStyle}
                    />
                    <p className="text-xs mt-1" style={{ color: '#4b5563' }}>Approximate travel time from major city</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── REVIEWS TAB ── */}
          {activeTab === 'reviews' && (
            <div className="space-y-4">
              <div className={sectionHeadingCls} style={sectionHeadingStyle}>
                <Icon name="Star" size={15} color="#f59e0b" />
                Buyer Reviews & Testimonials
              </div>
              <p className="text-xs" style={{ color: '#64748b' }}>Add initial buyer reviews that will appear in the "Reviews" tab of the buyer-facing modal.</p>

              {formData?.reviews?.length === 0 && (
                <div className="text-center py-8 rounded-xl border border-dashed" style={{ borderColor: '#1e293b' }}>
                  <Icon name="MessageSquare" size={32} color="#334155" />
                  <p className="text-sm mt-2" style={{ color: '#475569' }}>No reviews added yet</p>
                  <p className="text-xs mt-1" style={{ color: '#334155' }}>Click "Add Review" below to add buyer testimonials</p>
                </div>
              )}

              {formData?.reviews?.map((review, idx) => (
                <div key={idx} className="rounded-xl p-4 space-y-3 relative" style={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold" style={{ color: '#f59e0b' }}>Review #{idx + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeReview(idx)}
                      className="w-6 h-6 rounded-full flex items-center justify-center transition-colors hover:bg-red-900"
                      style={{ backgroundColor: '#7f1d1d22' }}
                    >
                      <Icon name="Trash2" size={12} color="#f87171" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls} style={labelStyle}>Reviewer Name</label>
                      <input
                        type="text"
                        value={review?.reviewerName}
                        onChange={e => updateReview(idx, 'reviewerName', e?.target?.value)}
                        placeholder="e.g., Rajesh Traders"
                        className={inputCls}
                        style={{ ...inputStyle, backgroundColor: '#0f172a' }}
                      />
                    </div>
                    <div>
                      <label className={labelCls} style={labelStyle}>Review Date</label>
                      <input
                        type="date"
                        value={review?.reviewDate}
                        onChange={e => updateReview(idx, 'reviewDate', e?.target?.value)}
                        className={inputCls}
                        style={{ ...inputStyle, backgroundColor: '#0f172a', colorScheme: 'dark' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls} style={labelStyle}>Rating</label>
                    <StarRating value={review?.rating} onChange={val => updateReview(idx, 'rating', val)} />
                  </div>

                  <div>
                    <label className={labelCls} style={labelStyle}>Review Text</label>
                    <textarea
                      value={review?.reviewText}
                      onChange={e => updateReview(idx, 'reviewText', e?.target?.value)}
                      placeholder="Write the buyer's review or testimonial here..."
                      rows={3}
                      className={inputCls + ' resize-none'}
                      style={{ ...inputStyle, backgroundColor: '#0f172a' }}
                    />
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addReview}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed text-sm font-semibold transition-colors"
                style={{ borderColor: '#f59e0b44', color: '#f59e0b', backgroundColor: '#f59e0b0a' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f59e0b18'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#f59e0b0a'; }}
              >
                <Icon name="Plus" size={16} color="#f59e0b" />
                Add Review
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-5 py-4 flex-shrink-0 rounded-b-2xl" style={{ backgroundColor: '#1e293b', borderTop: '1px solid #334155' }}>
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-colors"
            style={{ borderColor: '#334155', color: '#94a3b8', backgroundColor: 'transparent' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid || saving}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#f59e0b', color: '#000000' }}
          >
            <Icon name={saving ? 'Loader' : 'Check'} size={16} color="#000000" />
            {saving ? 'Uploading...' : editingFactory ? 'Save Changes' : 'Add Factory'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FactoryManagementTab = () => {
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [editingFactory, setEditingFactory] = useState(null);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [removeConfirm, setRemoveConfirm] = useState({ show: false, factoryId: null });

  useEffect(() => {
    const unsub = subscribeFactories((updated) => {
      setFactories(updated);
      setLoading(false);
    });
    return unsub;
  }, []);

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 3500);
  };

  const handleCSVUpload = async (rows) => {
    let updated = 0;
    let skipped = 0;
    let invalid = 0;
    const resultRows = [];

    for (const row of rows) {
      const remark = (row?.Remark || '')?.trim()?.toLowerCase();
      const factoryName = (row?.FactoryName || '')?.trim();
      const shortCode = (row?.FactoryShortCode || '')?.trim();
      const pincode = (row?.Factory_Pincode || '')?.trim();

      if (!factoryName) {
        invalid++;
        resultRows?.push({ status: 'Error', message: 'Missing required field: FactoryName' });
        continue;
      }

      if (!pincode || !/^\d{6}$/?.test(pincode)) {
        invalid++;
        resultRows?.push({ status: 'Error', message: 'Factory Pincode is mandatory (6-digit Indian pincode)' });
        continue;
      }

      if (!remark?.includes('update')) {
        skipped++;
        resultRows?.push({ status: 'Skipped', message: 'No changes — Remark is blank or not "update"' });
        continue;
      }

      const location = (row?.Location || '')?.trim();
      const state = (row?.State || '')?.trim();
      const csvStatus = (row?.Status || '')?.trim()?.toLowerCase() === 'inactive' ? 'Inactive' : 'Active';

      // Check if factory exists by shortCode or name
      let existingId = null;
      if (shortCode) {
        const { data } = await supabase?.from('factories')?.select('id')?.eq('short_code', shortCode)?.maybeSingle();
        existingId = data?.id || null;
      }
      if (!existingId) {
        const { data } = await supabase?.from('factories')?.select('id')?.ilike('name', factoryName)?.maybeSingle();
        existingId = data?.id || null;
      }

      if (existingId) {
        const { error: updateErr } = await supabase?.from('factories')?.update({
          name: factoryName,
          location: location || undefined,
          short_code: shortCode || undefined,
          pincode: pincode || undefined,
          state: state || undefined,
          status: csvStatus,
        })?.eq('id', existingId);
        if (updateErr) {
          console.error('Factory update error:', updateErr?.message);
          resultRows?.push({ status: 'Error', message: updateErr?.message });
          invalid++;
        } else {
          updated++;
          resultRows?.push({ status: 'Updated', message: 'Updated successfully' });
        }
      } else {
        const { error: insertErr } = await supabase?.from('factories')?.insert({
          name: factoryName,
          short_code: shortCode || null,
          location,
          state,
          pincode,
          status: csvStatus,
        });
        if (insertErr) {
          console.error('Factory insert error:', insertErr?.message);
          resultRows?.push({ status: 'Error', message: insertErr?.message });
          invalid++;
        } else {
          updated++;
          resultRows?.push({ status: 'Updated', message: 'New record added successfully' });
        }
      }
    }

    // Refresh factories list
    const all = await getFactories();
    setFactories(all);
    showNotification('success', `${updated} rows updated successfully, ${skipped} rows skipped.`);
    return { updated, skipped, invalid, resultRows };
  };

  const openAddForm = () => {
    setEditingFactory(null);
    setShowForm(true);
  };

  const openEditForm = (factory) => {
    setEditingFactory(factory);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingFactory(null);
  };

  const handleSave = async (data) => {
    if (editingFactory) {
      await updateFactoryById(editingFactory?.id, data);
      showNotification('success', 'Factory updated successfully.');
    } else {
      await addFactory({ ...data, status: 'Active' });
      showNotification('success', 'Factory added successfully.');
    }
    closeForm();
  };

  const handleToggleStatus = async (factory) => {
    const newStatus = factory?.status === 'Active' ? 'Inactive' : 'Active';
    await updateFactoryById(factory?.id, { ...factory, status: newStatus });
    showNotification('success', `Factory ${newStatus === 'Active' ? 'resumed' : 'paused'} successfully.`);
  };

  const handleRemove = (factoryId) => {
    setRemoveConfirm({ show: true, factoryId });
  };

  const confirmRemove = async () => {
    await removeFactory(removeConfirm?.factoryId);
    showNotification('success', 'Factory removed successfully.');
    setRemoveConfirm({ show: false, factoryId: null });
  };

  return (
    <div className="space-y-6">
      {showCSVModal && (
        <FactoryCSVUploadModal
          onClose={() => setShowCSVModal(false)}
          onUpload={handleCSVUpload}
        />
      )}
      {notification?.show && (
        <div className="fixed top-4 right-4 z-50 max-w-sm">
          <div className={`rounded-lg p-4 border shadow-lg flex items-center gap-3 ${notification?.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <Icon name={notification?.type === 'success' ? 'CheckCircle' : 'XCircle'} size={18} className={notification?.type === 'success' ? 'text-green-600' : 'text-red-600'} />
            <p className={`text-sm font-medium ${notification?.type === 'success' ? 'text-green-900' : 'text-red-900'}`}>{notification?.message}</p>
            <button onClick={() => setNotification({ show: false, type: '', message: '' })} className="ml-auto text-gray-400 hover:text-gray-600">
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      )}
      {removeConfirm?.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-sm w-full mx-4 shadow-xl">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold mb-1" style={{ color: '#1f2937' }}>Remove Factory</h3>
                <p className="text-sm" style={{ color: '#4b5563' }}>Are you sure you want to remove this factory? This cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setRemoveConfirm({ show: false, factoryId: null })} className="px-4 py-2 text-sm font-semibold rounded-lg border-2 border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={confirmRemove} className="px-4 py-2 text-sm font-bold rounded-lg bg-red-600 text-white border-2 border-red-700 hover:bg-red-700 transition-colors">Remove</button>
            </div>
          </div>
        </div>
      )}
      {showForm && (
        <RichFactoryFormModal
          editingFactory={editingFactory}
          onClose={closeForm}
          onSave={handleSave}
        />
      )}
      {/* Header Row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold" style={{ color: '#1f2937' }}>Factory Management</h2>
          <p className="text-sm mt-0.5" style={{ color: '#4b5563' }}>
            {factories?.length} registered factor{factories?.length !== 1 ? 'ies' : 'y'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={downloadSampleCSV} className="flex items-center gap-1.5 text-sm font-semibold underline" style={{ color: '#d97706' }}>
            <Icon name="Download" size={15} />
            Download Sample CSV
          </button>
          <button onClick={() => downloadCurrentDataCSV(factories)} className="flex items-center gap-1.5 text-sm font-semibold underline" style={{ color: '#16a34a' }}>
            <Icon name="FileDown" size={15} />
            Download Current Data CSV
          </button>
          <button onClick={() => setShowCSVModal(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-bold text-sm border-2 transition-colors hover:bg-amber-500" style={{ backgroundColor: '#f59e0b', color: '#000000', borderColor: '#d97706' }}>
            <Icon name="Upload" size={16} />
            Bulk Upload CSV
          </button>
          <button onClick={openAddForm} className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm border-2 transition-colors hover:bg-gray-100" style={{ backgroundColor: '#ffffff', color: '#374151', borderColor: '#d1d5db' }}>
            <Icon name="Plus" size={18} />
            Add New Factory
          </button>
        </div>
      </div>
      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-10">
          <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mr-3"></div>
          <p className="text-sm" style={{ color: '#6b7280' }}>Loading factories…</p>
        </div>
      )}
      {/* Factories Table */}
      {!loading && (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {/* Mobile Card Layout */}
        <div className="block md:hidden divide-y divide-gray-100">
          {factories?.length === 0 ? (
            <div className="p-8 text-center">
              <Icon name="Building2" size={40} className="text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium" style={{ color: '#6b7280' }}>No factories registered yet.</p>
              <button onClick={openAddForm} className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm border-2 mx-auto transition-colors" style={{ backgroundColor: '#f59e0b', color: '#000000', borderColor: '#d97706' }}>
                <Icon name="Plus" size={16} />Add First Factory
              </button>
            </div>
          ) : (
            factories?.map((factory) => (
              <div key={factory?.id} className="p-4">
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
                  <div>
                    <div className="font-bold text-base" style={{ color: '#1f2937' }}>{factory?.name}</div>
                    {factory?.shortCode && <div className="text-xs font-mono mt-0.5" style={{ color: '#d97706' }}>{factory?.shortCode}</div>}
                    <div className="text-sm mt-0.5 flex items-center gap-1" style={{ color: '#6b7280' }}>
                      <Icon name="MapPin" size={13} />
                      {factory?.location}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${factory?.status === 'Active' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${factory?.status === 'Active' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                      {factory?.status}
                    </span>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => openEditForm(factory)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg border-2 border-blue-500 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors">
                      <Icon name="Edit" size={13} />Edit
                    </button>
                    <button onClick={() => handleToggleStatus(factory)} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg border-2 transition-colors ${factory?.status === 'Active' ? 'border-orange-500 text-orange-700 bg-orange-50 hover:bg-orange-100' : 'border-green-500 text-green-700 bg-green-50 hover:bg-green-100'}`} title={factory?.status === 'Active' ? 'Pause Factory' : 'Resume Factory'}>
                      <Icon name={factory?.status === 'Active' ? 'PauseCircle' : 'PlayCircle'} size={13} />
                      {factory?.status === 'Active' ? 'Pause' : 'Resume'}
                    </button>
                    <button onClick={() => handleRemove(factory?.id)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg border-2 border-red-500 text-red-700 bg-red-50 hover:bg-red-100 transition-colors">
                      <Icon name="Trash2" size={13} />Remove
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Desktop Table Layout */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#374151' }}>Short Code</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#374151' }}>Factory Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#374151' }}>Location</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#374151' }}>Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#374151' }}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {factories?.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <Icon name="Building2" size={40} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-medium" style={{ color: '#6b7280' }}>No factories registered yet.</p>
                    <button onClick={openAddForm} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm border-2 transition-colors" style={{ backgroundColor: '#f59e0b', color: '#000000', borderColor: '#d97706' }}>
                      <Icon name="Plus" size={16} />Add First Factory
                    </button>
                  </td>
                </tr>
              ) : (
                factories?.map((factory) => (
                  <tr key={factory?.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-sm font-mono font-semibold" style={{ color: '#d97706' }}>{factory?.shortCode || '—'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold" style={{ color: '#1f2937' }}>{factory?.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-sm" style={{ color: '#4b5563' }}>
                        <Icon name="MapPin" size={14} className="text-gray-400" />
                        {factory?.location}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${factory?.status === 'Active' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${factory?.status === 'Active' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                        {factory?.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEditForm(factory)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border-2 border-blue-500 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors" title="Edit Factory">
                          <Icon name="Edit" size={13} />Edit
                        </button>
                        <button onClick={() => handleToggleStatus(factory)} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border-2 transition-colors ${factory?.status === 'Active' ? 'border-orange-500 text-orange-700 bg-orange-50 hover:bg-orange-100' : 'border-green-500 text-green-700 bg-green-50 hover:bg-green-100'}`} title={factory?.status === 'Active' ? 'Pause Factory' : 'Resume Factory'}>
                          <Icon name={factory?.status === 'Active' ? 'PauseCircle' : 'PlayCircle'} size={13} />
                          {factory?.status === 'Active' ? 'Pause' : 'Resume'}
                        </button>
                        <button onClick={() => handleRemove(factory?.id)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border-2 border-red-500 text-red-700 bg-red-50 hover:bg-red-100 transition-colors" title="Remove Factory">
                          <Icon name="Trash2" size={13} />Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
};

export default FactoryManagementTab;

